'use client';

import React, { useRef, useEffect, useState } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import AppImage from '@/components/ui/AppImage';

interface StatItem {
  value: string;
  label: string;
  description: string;
}

interface BenefitItem {
  icon: string;
  title: string;
  description: string;
  highlight?: string;
}

interface StepItem {
  number: string;
  title: string;
  description: string;
}

interface TestimonialItem {
  name: string;
  location: string;
  earnings: string;
  quote: string;
  image: string;
  alt: string;
}

const stats: StatItem[] = [
  { value: 'UGX 0', label: 'Listing fee', description: 'Free to list your property — no upfront costs, ever.' },
  { value: '14,000+', label: 'Active listings', description: 'Hosts across Africa already earning on Ndoto.' },
  { value: '200+', label: 'Cities covered', description: 'From Kampala to Cape Town, guests are searching near you.' },
  { value: '4.8★', label: 'Host satisfaction', description: 'Our hosts rate their experience with Ndoto.' },
];

const benefits: BenefitItem[] = [
  {
    icon: '💰',
    title: 'Set your own price',
    description: 'You decide what to charge per night. Adjust pricing for weekends, seasons, or special events — full control is yours.',
    highlight: 'Avg. host earns UGX 1.2M/month',
  },
  {
    icon: '🛡️',
    title: 'Host protection included',
    description: 'Every booking comes with Ndoto Host Guarantee — covering property damage up to UGX 50M at no extra cost.',
    highlight: 'Up to UGX 50M coverage',
  },
  {
    icon: '📅',
    title: 'Flexible availability',
    description: 'Block dates when you need your space. Host full-time or just a few nights a month — your schedule, your rules.',
  },
  {
    icon: '💳',
    title: 'Fast, reliable payouts',
    description: 'Get paid within 24 hours of guest check-in via mobile money, bank transfer, or your preferred method.',
    highlight: 'Paid within 24 hours',
  },
  {
    icon: '📊',
    title: 'Smart pricing insights',
    description: 'Our dashboard shows you local demand trends and competitor rates so you can price confidently.',
  },
  {
    icon: '🤝',
    title: 'Dedicated host support',
    description: 'A real person is available 7 days a week to help you with listings, guests, or anything in between.',
    highlight: '7-day support',
  },
];

const steps: StepItem[] = [
  {
    number: '01',
    title: 'Create your listing',
    description: 'Add photos, describe your space, and set your house rules. Takes about 10 minutes.',
  },
  {
    number: '02',
    title: 'Set your availability',
    description: 'Choose which dates your property is open. You can update this anytime from your dashboard.',
  },
  {
    number: '03',
    title: 'Welcome your first guest',
    description: 'Once a guest books, you\'ll get a notification. Confirm, communicate, and prepare for their arrival.',
  },
  {
    number: '04',
    title: 'Get paid',
    description: 'After check-in, your payout is released automatically — no chasing invoices.',
  },
];

const testimonials: TestimonialItem[] = [
  {
    name: 'Amara Diallo',
    location: 'Kampala, Uganda',
    earnings: 'UGX 2.4M/month',
    quote: 'I listed my spare room on a Friday and had my first guest by Sunday. Ndoto made it incredibly simple — the support team walked me through everything.',
    image: 'https://images.unsplash.com/photo-1531123897727-8f129e1688ce?w=200&q=80',
    alt: 'Amara Diallo, a host from Kampala smiling confidently',
  },
  {
    name: 'Kwame Asante',
    location: 'Accra, Ghana',
    earnings: 'UGX 3.8M/month',
    quote: 'I was skeptical at first, but the host guarantee gave me confidence. Now I manage three listings and it\'s become my main income.',
    image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&q=80',
    alt: 'Kwame Asante, a host from Accra standing in front of his property',
  },
  {
    name: 'Fatima Nkosi',
    location: 'Nairobi, Kenya',
    earnings: 'UGX 1.9M/month',
    quote: 'The pricing insights tool alone is worth it. I increased my nightly rate by 30% after seeing what similar properties were charging nearby.',
    image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=200&q=80',
    alt: 'Fatima Nkosi, a host from Nairobi reviewing her hosting dashboard',
  },
];

function AnimatedSection({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} ${className}`}
    >
      {children}
    </div>
  );
}

export default function HostLearnMorePage() {
  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero */}
      <section className="relative h-[70vh] min-h-[500px] flex items-center">
        <AppImage
          src="https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1600&q=80"
          alt="Stunning African villa with a private pool surrounded by lush greenery, perfect for hosting guests"
          fill
          className="object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/50 to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 w-full">
          <div className="max-w-xl">
            <span className="inline-block bg-[#4088C6]/20 border border-[#4088C6]/40 text-[#7BB8E8] text-xs font-semibold uppercase tracking-widest px-3 py-1 rounded-full mb-4">
              For Property Owners
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-5">
              Your property.<br />Your income.<br />Your terms.
            </h1>
            <p className="text-white/80 text-lg mb-8 leading-relaxed">
              Ndoto connects African property owners with guests who are ready to pay. List for free, earn on your schedule, and keep full control of your space.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                href="/host"
                className="bg-[#4088C6] text-white font-bold px-8 py-4 rounded-xl hover:bg-[#3070a8] transition-colors text-sm text-center"
              >
                Start listing — it's free
              </Link>
              <a
                href="#how-it-works"
                className="border border-white/60 text-white font-semibold px-8 py-4 rounded-xl hover:bg-white/10 transition-colors text-sm text-center"
              >
                See how it works
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats bar */}
      <section className="bg-gray-950 py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <p className="text-3xl font-bold text-white mb-1">{stat.value}</p>
                <p className="text-[#4088C6] font-semibold text-sm mb-1">{stat.label}</p>
                <p className="text-gray-400 text-xs leading-snug">{stat.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 px-4 sm:px-6 max-w-7xl mx-auto">
        <AnimatedSection>
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Why hosts choose Ndoto</h2>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto">
              We built Ndoto specifically for African hosts — the tools, protections, and support you actually need.
            </p>
          </div>
        </AnimatedSection>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {benefits.map((benefit, i) => (
            <AnimatedSection key={benefit.title}>
              <div
                className="bg-gray-50 rounded-2xl p-7 h-full border border-gray-100 hover:border-[#4088C6]/30 hover:shadow-md transition-all duration-300"
                style={{ transitionDelay: `${i * 60}ms` }}
              >
                <span className="text-3xl mb-4 block">{benefit.icon}</span>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{benefit.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed mb-3">{benefit.description}</p>
                {benefit.highlight && (
                  <span className="inline-block bg-[#4088C6]/10 text-[#4088C6] text-xs font-semibold px-3 py-1 rounded-full">
                    {benefit.highlight}
                  </span>
                )}
              </div>
            </AnimatedSection>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="py-20 bg-gray-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <AnimatedSection>
            <div className="text-center mb-14">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">From listing to earning in 4 steps</h2>
              <p className="text-gray-400 text-lg max-w-xl mx-auto">
                No complicated setup. No hidden fees. Just a straightforward path to extra income.
              </p>
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((step, i) => (
              <AnimatedSection key={step.number}>
                <div
                  className="relative"
                  style={{ transitionDelay: `${i * 80}ms` }}
                >
                  {i < steps.length - 1 && (
                    <div className="hidden lg:block absolute top-8 left-full w-full h-px bg-gradient-to-r from-[#4088C6]/40 to-transparent z-0" />
                  )}
                  <div className="bg-gray-900 rounded-2xl p-7 border border-gray-800 hover:border-[#4088C6]/40 transition-colors relative z-10">
                    <span className="text-5xl font-black text-[#4088C6]/20 block mb-3 leading-none">{step.number}</span>
                    <h3 className="text-white font-bold text-base mb-2">{step.title}</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">{step.description}</p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-4 sm:px-6 max-w-7xl mx-auto">
        <AnimatedSection>
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Hosts who made the leap</h2>
            <p className="text-gray-500 text-lg max-w-xl mx-auto">
              Real stories from real hosts across Africa.
            </p>
          </div>
        </AnimatedSection>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {testimonials.map((t, i) => (
            <AnimatedSection key={t.name}>
              <div
                className="bg-white rounded-2xl p-7 border border-gray-100 shadow-sm hover:shadow-md transition-shadow h-full flex flex-col"
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <p className="text-gray-700 text-sm leading-relaxed mb-6 flex-1">"{t.quote}"</p>
                <div className="flex items-center gap-4">
                  <div className="relative w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
                    <AppImage src={t.image} alt={t.alt} fill className="object-cover" />
                  </div>
                  <div>
                    <p className="font-bold text-gray-900 text-sm">{t.name}</p>
                    <p className="text-gray-400 text-xs">{t.location}</p>
                    <p className="text-[#4088C6] text-xs font-semibold mt-0.5">{t.earnings}</p>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 sm:px-6">
        <AnimatedSection>
          <div className="max-w-3xl mx-auto text-center bg-gradient-to-br from-[#4088C6] to-[#2a6099] rounded-3xl p-12 md:p-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Ready to start earning?</h2>
            <p className="text-white/80 text-lg mb-8">
              Listing is free. Your first guest could be booking tonight.
            </p>
            <Link
              href="/host"
              className="inline-block bg-white text-[#4088C6] font-bold px-10 py-4 rounded-xl hover:bg-gray-50 transition-colors text-sm"
            >
              Create your listing — free
            </Link>
          </div>
        </AnimatedSection>
      </section>

      <Footer />
    </div>
  );
}
